/**
 * 全局上传进度（2026-10-22 用户需求）。
 *
 * 需求原话：「上传时最好是屏幕右上角有一个上传进度」。
 *
 * 背景：原来进度只在个别入口有，而且是局部的 ——
 *   · 交付物卡片拖拽上传：队列条挂在卡片里（ProjectDrawer 的 UploadQueue）；
 *   · 产物附件区「上传图纸」：卡片里一条细进度条 + 按钮文案「上传中 42%」；
 *   · 阶段任务卡、City 结论附件、评论附件：**完全没有进度**，点了按钮只能干等。
 * 用户的实际痛点不是「某个入口缺进度」，而是**上传这个动作本身不可见**：
 * 大文件传 30 秒，页面上没有任何反馈，看起来像卡死，于是反复点、反复重传
 * （这与用户「上传三次都没成功」的反馈是同一个体验问题）。
 *
 * 做法：不做「每个入口各加一条进度」，而是**在 axios 层统一接管所有文件上传**。
 *   1) 请求拦截器识别 FormData 请求，自动挂一个 onUploadProgress 上报到本模块；
 *   2) 一个全局浮层组件订阅这里的状态，固定在屏幕右上角渲染；
 *   3) 各调用点**一行都不用改** —— 已有的局部进度条照旧工作（用户在那块区域看细节），
 *      全局浮层提供「上传正在进行 / 进度多少 / 是否失败」的统一可见性。
 *
 * 关键取舍：
 *   · 为什么不只在设计模块加：用户是在设计项目里提的，但「上传没有进度」是全站问题
 *     （聊天、协作、财务导入、CRM 上传都是同一个 axios 实例）。放 axios 层一次解决，
 *     且以后新增的上传入口自动拥有进度，不需要谁记得去接。
 *   · 为什么是右上角：用户明确要求右上角；那里已有提示卡（sonner），
 *     所以浮层与提示卡的 offset 对齐，两者上下错开、互不遮挡。
 *   · 小文件不打扰：小于 SMALL_BYTES 的上传（图标、头像、小截图）通常 1 秒内完成，
 *     立刻闪一下进度反而造成视觉噪音。这类上传延迟一小段时间再显示，
 *     传得快就根本不出现。
 *   · 失败也要可见：上传失败时浮层短暂转为失败态并显示原因，然后自动收起；
 *     同时调用方原有的 toast.error 仍会照常弹出，不重复也不冲突。
 */

/** 小于这个体积的上传不显示浮层（传得太快，闪一下只是噪音）。 */
const SMALL_BYTES = 1024 * 1024; // 1MB
/** 小文件延迟多久才显示（超过这个时间还没传完，说明确实慢，需要让用户看到）。 */
const SHOW_DELAY = 700;
/** 成功后浮层保留多久（让用户看到「完成」而不是突然消失）。 */
const DONE_LINGER = 1200;
/** 失败后浮层保留多久（够看清原因，之后由调用方的 toast 继续承载信息）。 */
const FAIL_LINGER = 3000;

let seq = 0;
const jobs = new Map(); // id -> job
const listeners = new Set();

const snapshot = () => ({
  // 按开始时间排序，先传的先显示，避免列表跳动。
  items: [...jobs.values()].sort((a, b) => a.startedAt - b.startedAt),
});

let cached = snapshot();
const emit = () => {
  cached = snapshot();
  listeners.forEach((fn) => { try { fn(cached); } catch { /* 订阅方异常不影响上传 */ } });
};

export const subscribeUploads = (fn) => { listeners.add(fn); fn(cached); return () => listeners.delete(fn); };
export const getUploads = () => cached;

const patch = (id, next) => {
  const cur = jobs.get(id);
  if (!cur) return;
  jobs.set(id, { ...cur, ...next });
  emit();
};

const remove = (id) => { if (jobs.delete(id)) emit(); };

/**
 * 登记一次上传，返回 { id, update, done, fail, cancel }。
 * 由 axios 拦截器调用；业务代码不需要直接用。
 *
 * @param {object} o
 * @param {string} o.label   展示用的名字（通常是文件名或接口语义）
 * @param {number} o.size    总字节数（未知时传 0，进度按已上传量展示）
 */
export const trackUpload = ({ label = "", size = 0, url = "" } = {}) => {
  const id = `up-${++seq}`;
  const job = {
    id, label, size, url,
    loaded: 0,
    pct: 0,
    status: "uploading", // uploading | done | error
    error: "",
    startedAt: Date.now(),
    // 小文件先不显示，等 SHOW_DELAY 到点还没结束再露面。
    hidden: size > 0 && size < SMALL_BYTES,
  };
  jobs.set(id, job);
  if (job.hidden) {
    job.timer = setTimeout(() => { const cur = jobs.get(id); if (cur && cur.status === "uploading") patch(id, { hidden: false }); }, SHOW_DELAY);
  }
  emit();

  const clearTimer = () => { if (job.timer) { clearTimeout(job.timer); job.timer = null; } };

  return {
    id,
    /** 进度上报：loaded / total 由 axios 给出。 */
    update: ({ loaded = 0, total = 0 } = {}) => {
      const cur = jobs.get(id);
      if (!cur || cur.status !== "uploading") return;
      const size2 = total || cur.size || 0;
      patch(id, {
        loaded, size: size2,
        pct: size2 > 0 ? Math.min(99, Math.round((loaded / size2) * 100)) : 0,
        // 一旦有真实进度就说明确实在传，小文件也应该露出来。
        hidden: false,
      });
    },
    done: () => {
      clearTimer();
      const cur = jobs.get(id);
      if (!cur) return;
      // 本来就没露面的小文件：直接静默移除，不要闪一下「完成」。
      if (cur.hidden) { remove(id); return; }
      patch(id, { status: "done", pct: 100, loaded: cur.size || cur.loaded, hidden: false });
      setTimeout(() => remove(id), DONE_LINGER);
    },
    fail: (msg = "") => {
      clearTimer();
      const cur = jobs.get(id);
      if (!cur) return;
      if (cur.hidden && !msg) { remove(id); return; }
      patch(id, { status: "error", error: msg, hidden: false });
      setTimeout(() => remove(id), FAIL_LINGER);
    },
    cancel: () => { clearTimer(); remove(id); },
  };
};

/** 从 FormData 里挑一个能代表这次上传的名字（优先文件名）。 */
export const labelFromFormData = (data) => {
  try {
    for (const [, v] of data.entries()) {
      if (typeof File !== "undefined" && v instanceof File) return v.name || "";
    }
    // 非文件表单（少量文本字段）不必展示，交给调用方语义。
  } catch { /* 某些环境不支持 entries，忽略 */ }
  return "";
};

/** 从 FormData 里估算总字节数（多文件时求和）。 */
export const sizeFromFormData = (data) => {
  try {
    let total = 0;
    for (const [, v] of data.entries()) {
      if (typeof File !== "undefined" && v instanceof File) total += v.size || 0;
    }
    return total;
  } catch { return 0; }
};

/**
 * 清空所有上传任务（仅供测试与「退出登录」这类需要立刻收口的场景）。
 *
 * 为什么需要：完成 / 失败的任务会按 DONE_LINGER / FAIL_LINGER 停留一会儿再自动移除，
 * 单测里前一个用例的残留卡片会污染后一个用例的断言。生产代码不调用它 ——
 * 正常流程由 done() / fail() 自己的定时器收口。
 */
export const resetUploads = () => {
  jobs.forEach((j) => { if (j.timer) clearTimeout(j.timer); });
  jobs.clear();
  emit();
};
